import { FormItem, Modal, TextField } from '@getsynapse/design-system';
import { ModalProps } from '@getsynapse/design-system/dist/Organisms/Modal/Modal';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch } from 'react-redux';
import { renameBudget } from 'state/BudgetList/budgetListSlice';
import { LONG_INPUTS_LENGTH } from 'utils/constants';

export type RenameBudgetModalProps = ModalProps & {
  id: string;
  onRenameBudget: (success: boolean, data?: any) => void;
  onCancel: () => void;
  currentName: string;
};

export type RenameBudgetFormFields = {
  name: string;
};

export const RenameBudgetModal = ({
  onRenameBudget,
  onCancel,
  id,
  currentName,
  ...props
}: RenameBudgetModalProps) => {
  const dispatch = useDispatch();

  const [fieldsValues, setFieldsValues] = useState<RenameBudgetFormFields>({
    name: currentName,
  });

  useEffect(() => {
    setFieldsValues({ name: currentName });
  }, [currentName]);

  const canSubmit = useMemo(() => {
    return fieldsValues.name.trim().length > 0;
  }, [fieldsValues]);

  const handleRenameBudget = useCallback(async () => {
    const data = await dispatch(
      renameBudget({
        id,
        title: fieldsValues.name,
      })
    );
    const updatedBudget = get(data, 'payload', null);
    if (get(data, 'error')) {
      onRenameBudget(false, updatedBudget);
    } else {
      onRenameBudget(true, updatedBudget);
    }
  }, [dispatch, fieldsValues.name, id, onRenameBudget]);

  return (
    <Modal
      {...props}
      data-cy={`rename-budget-modal__${id}`}
      aria-label={intl.get('BUDGET_PAGE.RENAME_BUDGET_MODAL.TITLE')}
      titleIcon={{
        name: 'pencil-outline',
        className: 'text-xs',
      }}
      title={intl.get('BUDGET_PAGE.RENAME_BUDGET_MODAL.TITLE')}
      size='small'
      childrenClassName='pr-0'
      actionButtons={[
        {
          children: intl.get('BUDGET_PAGE.RENAME_BUDGET_MODAL.SUBMIT'),
          onClick: handleRenameBudget,
          variant: 'primary',
          hasDisabledStyle: !canSubmit,
        },
        {
          children: intl.get('CANCEL'),
          variant: 'tertiary',
          onClick: onCancel,
        },
      ]}
    >
      <div className='flex flex-col gap-y-4'>
        <FormItem>
          <TextField
            labelProps={{ required: true }}
            label={intl.get('BUDGET_PAGE.CREATE_BUDGET_MODAL.FORM.NAME')}
            displayCharactersCount
            maxLength={LONG_INPUTS_LENGTH}
            defaultValue={fieldsValues.name}
            onChange={(e: any) => setFieldsValues({ name: e.target.value })}
            placeholder={intl.get(
              'BUDGET_PAGE.CREATE_BUDGET_MODAL.PLACEHOLDERS.NAME'
            )}
            className='w-full'
          />
        </FormItem>
      </div>
    </Modal>
  );
};
